import JsonForm from "@rjsf/core";
import jsonFormValidator from "@rjsf/validator-ajv8";
import Hierarchy from "../../ImageViewerHandlers/Hierarchy/Hierarchy";
import React, {useEffect, useState} from "react";
import {Alert, AutoComplete, Button, Checkbox, Form, message, Modal, Progress, Select, Space, Steps, Table} from "antd";
import {
    useKHExtBlackboard,
    useKHExtJsonTyped,
    WorkspaceIdSource
} from "../../../contexts/KHExtensionsContext/KHExtensionsContext";
import {Loading} from "../../../hooks/useFetch";
import "./BulkUpdateHierarchyFromScriptModal.less";
import HierarchiesService, {HierarchyLog} from "../../../services/HierarchiesService";
import {UpdateJobProgress} from "../../../Utilities";
import {ColumnsType} from "antd/es/table";
import {LogOperationsTable} from "./HierarchyHistoryModal";
import {INodeToAdd} from "../../ImageViewerHandlers/Hierarchy/NodeToAdd";
import {getAttributesForNode} from "../../ImageViewerHandlers/ImageViewerObject";
import ObjectReference from "../../ImageViewerHandlers/Hierarchy/ObjectReference";
import HierarchyNode from "../../ImageViewerHandlers/Hierarchy/HierarchyNode";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {WidgetProps} from "@rjsf/utils";

export function BulkUpdateHierarchyFromScriptModal(
    {wsId, hierarchy, setVisible, visible, onOk}: {
        wsId: WorkspaceIdSource,
        hierarchy: Hierarchy,
        setVisible: React.Dispatch<React.SetStateAction<boolean>>
        visible: boolean,
        onOk: () => any
    }
) {
    return (
        <Modal
            width={1200}
            maskClosable={false}
            title={`Custom Bulk Update of ${hierarchy.name}`}
            visible={visible}
            onCancel={() => setVisible(false)}
            destroyOnClose={true}
            footer={null}
        >
            <Body wsId={wsId} hierarchy={hierarchy} onOk={onOk}/>
        </Modal>
    );
}

const StepNames = {
    SelectTemplate: 0,
    Review: 1,
    Results: 2,
}

function Body(
    {wsId, hierarchy, onOk}: {
        wsId: WorkspaceIdSource,
        hierarchy: Hierarchy,
        onOk: () => void,
    }
) {
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [formData, setFormData] = useState<any>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progress, setProgress] = useState<any>(null);
    const [error, setError] = useState<string | undefined>(undefined);
    const [result, setResult] = useState<any | undefined>(undefined);
    const [resultJson, setResultJson] = useState<string | undefined>(undefined);
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [log, setLog] = useState<HierarchyLog | undefined>(undefined);
    const [selectedCommands, setSelectedCommands] = useState<string[]>([]);
    const blackboard = useKHExtBlackboard();
    const templates = useTemplates(wsId, 1);

    useEffect(() => {
        if (result) {
            setSelectedCommands(result.map((cmd: CommandModel) => cmd.id));
            setResultJson(JSON.stringify(result));
        }
    }, [result]);

    const fetchTaskResult = (taskId: string) => {
        const updateProgress = new UpdateJobProgress().withDefaultUpdateProgress(setProgress);

        setError(undefined);
        setResult(undefined);

        return updateProgress.do(() => HierarchiesService.getBulkUpdateHierarchyFromScriptPreviewProgress(hierarchy.id, taskId))
            .then((response) => {
                if (response.completed) {
                    message.success("Preview generated successfully");
                    return HierarchiesService.getBulkUpdateHierarchyFromScriptPreviewResult(
                        hierarchy.id,
                        taskId
                    );
                }
                return null;
            })
            .then((result) => {
                if (result) {
                    console.log(result);
                    const commandsWithIds = result.map((cmd: CommandModel) => ({ ...cmd, id: uuidv4() }));
                    setResult(commandsWithIds);
                    setSelectedCommands(commandsWithIds.map((cmd: CommandModel) => cmd.id));
                    setProgress({
                        status: "Completed",
                        percent: 100
                    });
                    setCurrentStep(StepNames.Review);
                }
            })
            .catch((error: Error) => {
                console.error(error);
                setError(error.toString());
                message.error("Failed to check progress");
            });
    };

    const handleTemplateSelect = (value: string) => {
        setSelectedTemplateId(value);
        setFormData(null);
    };

    if (templates.status === "loading") {
        return <div>Loading...</div>;
    }

    if (templates.status === "error") {
        return <div>Error loading templates</div>;
    }

    if (!templates.data || templates.data.length === 0) {
        return <div>No templates found</div>;
    }

    const handleFormChange = (data: any) => {
        setFormData(Object.fromEntries(
            Object.entries(data).map(([k, v]) => [k, v ?? null])
        ));
    };

    const handleGeneratePreview = async () => {
        if (isSubmitting) {
            return;
        }

        if (!selectedTemplateId) {
            message.error('Please select a template');
            return;
        }

        if (!formData) {
            message.error('Please fill in the form');
            return;
        }

        setIsSubmitting(true);
        try {
            const context: { selected_nodes: any[], selected_objects: any[], hierarchy_id: string } = {
                selected_nodes: [],
                selected_objects: [],
                hierarchy_id: hierarchy.id
            };
            const buffer = blackboard.get<ReadonlyArray<INodeToAdd>>("hierarchy/add_multiple_nodes_buffer");
            for (let item of buffer) {
                const node = item.getRelatedNode(hierarchy);
                if (node == null) continue;
                context.selected_nodes.push(node.getDict());
            }
            for (let item of buffer) {
                if (item.isObjectToAdd()) {
                    const objectCenter = item.target.getRect().getCenter();
                    context.selected_objects.push({
                        "self": item.target.getDict(),
                        "possible_attributes": getAttributesForNode(item.target),
                        "possible_references": [
                            new ObjectReference({
                                id: null,
                                page_id: hierarchy.imageViewer.state.pageId!,
                                x_rel: objectCenter.x / hierarchy.imageViewer.imageWidth!,
                                y_rel: objectCenter.y / hierarchy.imageViewer.imageHeight!,
                                node: null as unknown as HierarchyNode,
                            }).getDict()
                        ],
                    });
                }
            }
            const taskId = await HierarchiesService.bulkUpdateHierarchyFromScriptPreview(
                hierarchy.id,
                selectedTemplateId,
                formData,
                context
            );
            await fetchTaskResult(taskId);
        } catch (error) {
            console.error('Error submitting form:', error);
            message.error('Failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedTemplate = templates.data.find(t => t.id === selectedTemplateId);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Steps current={currentStep} onChange={setCurrentStep}>
                <Steps.Step title="Select a template"/>
                <Steps.Step title="Review & Run" disabled={result == null}/>
                <Steps.Step title="Results" disabled={log == null}/>
            </Steps>

            <StepContent
                current={currentStep}
                target={StepNames.SelectTemplate}
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select a template"
                        onChange={handleTemplateSelect}
                        value={selectedTemplateId || undefined}
                    >
                        {templates.data.map((template) => (
                            <Select.Option key={template.id} value={template.id}>
                                {template.name || template.key}
                            </Select.Option>
                        ))}
                    </Select>
                    {selectedTemplate && (
                        <div>
                            <UserArgsForm
                                id={`template-form-${selectedTemplate.id}`}
                                schema={selectedTemplate.params_schema}
                                uiSchema={selectedTemplate.params_ui_schema}
                                value={formData}
                                onChange={handleFormChange}
                            />
                            <Button
                                type="primary"
                                onClick={handleGeneratePreview}
                                loading={isSubmitting}
                                style={{ marginTop: 16 }}
                            >
                                Generate Preview
                            </Button>
                        </div>
                    )}
                    {progress && (
                        <div style={{ marginTop: 16 }}>
                            <h4>Generating Preview: {progress.status}</h4>
                            <Progress percent={progress.percent} />
                        </div>
                    )}
                    {error != null && (
                        <Alert message={error} type={"error"}/>
                    )}
                </Space>
            </StepContent>

            <StepContent
                current={currentStep}
                target={StepNames.Review}
            >
                {result != null && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <PreviewResultsTable
                            hierarchy={hierarchy}
                            result={result} 
                            selectedCommands={selectedCommands}
                            onSelectionChange={setSelectedCommands}
                        />
                        <RunBulkUpdate
                            log={log}
                            setLog={setLog}
                            hierarchy={hierarchy}
                            commands={selectedCommands}
                            allCommands={result}
                            onSuccess={() => {
                                setCurrentStep(StepNames.Results);
                                onOk();
                            }}
                        />
                    </Space>
                )}
            </StepContent>

            <StepContent
                current={currentStep}
                target={StepNames.Results}
            >
                {log != null && (
                    <LogOperationsTable
                        log={log}
                        hierarchyId={hierarchy.id}
                    />
                )}
            </StepContent>
        </Space>
    );
}

function StepContent(
    {current, target, children, destroyOnClose=false}:
    {current: number, target: number, children: React.ReactNode, destroyOnClose?: boolean}
) {
    const shouldRender = current === target;
    if (destroyOnClose) {
        return shouldRender ? <>{children}</> : null;
    } else {
        return (
            <div style={{display: shouldRender ? 'block' : 'none'}}>
                {children}
            </div>
        );
    }
}

export function RunBulkUpdate({hierarchy, commands, onSuccess, log, setLog, allCommands}: {
    hierarchy: Hierarchy,
    commands: string[],
    onSuccess: () => void,
    log: HierarchyLog | undefined,
    setLog: React.Dispatch<React.SetStateAction<HierarchyLog | undefined>>,
    allCommands: CommandModel[]
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progress, setProgress] = useState<any>(null);
    const [error, setError] = useState<string | undefined>(undefined);
    const [isCompleted, setIsCompleted] = useState(false);

    const runBulkUpdate = async () => {
        if (isSubmitting || isCompleted) {
            return;
        }

        setIsSubmitting(true);

        const fetchTaskResult = (taskId: string) => {
            const updateProgress = new UpdateJobProgress().withDefaultUpdateProgress(setProgress);
            setError(undefined);
            setLog(undefined);
            return updateProgress.do(() => HierarchiesService.getBulkUpdateHierarchyFromJsonProgress(hierarchy.id, taskId))
                .then((response) => {
                    if (response.completed) {
                        message.success("Preview generated successfully");
                        return HierarchiesService.getBulkUpdateHierarchyFromJsonResult(
                            hierarchy.id,
                            taskId
                        );
                    }
                    throw new Error("Failed to bulk update");
                })
                .then((result) => {
                    return HierarchiesService.getHierarchyLog(hierarchy.id, result.log_id);
                })
                .then(response => {
                    if (response?.data) {
                        setLog(response.data);
                        setProgress({ status: "Completed", percent: 100 });
                        setIsCompleted(true);
                        onSuccess();
                    } else {
                        throw new Error('No log data received');
                    }
                })
                .catch((error: Error) => {
                    console.error(error);
                    setError(error.toString());
                    message.error('Failed to bulk update');
                    setIsCompleted(false);
                })
        }

        try {
            const commandsToSend = allCommands.filter(cmd => commands.includes(cmd.id));
            const taskId = await HierarchiesService.bulkUpdateHierarchyFromJson(hierarchy.id, commandsToSend);
            await fetchTaskResult(taskId);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <Space direction={"vertical"} style={{ width: '100%' }}>
            <Button
                type="primary"
                onClick={runBulkUpdate}
                loading={isSubmitting}
                disabled={isCompleted}
                style={{ marginTop: 16 }}
            >
                Run Bulk Update
            </Button>
            {progress && (
                <div style={{ marginTop: 16 }}>
                    <h4>Progress: {progress.status}</h4>
                    <Progress 
                        percent={progress.percent} 
                        status={progress.status === "Completed" ? "success" : "active"}
                        format={percent => `${percent}%`}
                    />
                </div>
            )}
            {error != null && (
                <Alert message={error} type={"error"}/>
            )}
        </Space>
    );
}

export const RJSFAutocomplete: React.FC<WidgetProps> = (
    {
        id,
        uiSchema,
        value,
        onChange,
    }
) => {
    return (
        <Form.Item>
            <AutoComplete
                id={id}
                defaultValue={uiSchema?.["default-value"] ?? ""}
                options={(uiSchema?.["options"] ?? []).map((x: string) => ({value: x}))}
                value={value}
                onChange={v => onChange(v)}
                filterOption={(inputValue, option) => option?.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
            />
        </Form.Item>
    );
};

type UserArgsFormProps = {
    id: string,
    schema: any,
    uiSchema: any,
    value?: any,
    onChange?: (value: any) => void,
};

export function UserArgsForm({id, value, onChange, schema, uiSchema}: UserArgsFormProps) {
    return (
        <JsonForm
            id={id}
            className={"templates-form"}
            widgets={{
                autocomplete: RJSFAutocomplete
            }}
            schema={schema}
            validator={jsonFormValidator}
            formData={value}
            onChange={v => {
                onChange?.(v.formData);
            }}
            uiSchema={uiSchema ?? {}}
            children={true}
        />
    );
}

type Template = {
    id: string,
    key: string,
    name: string,
    params_schema: any,
    params_ui_schema: any,
}

function useTemplates(wsId: WorkspaceIdSource, version: number): Loading<Template[]> {
    return useKHExtJsonTyped<Template>(
        wsId,
        "hierarchy-custom-bulk-update-template",
        version,
        x => ({
            id: x.id,
            ...x.data,
        }) as Template
    );
}

interface UniversalNodeIdModel {
    text: string;
    label: string;
}

interface HierarchyNodeAttributeModel {
    key: string;
    value: string;
}

interface DrawingObjectReferenceModel {
    id?: string | null;
    page_id: string;
    x_rel: number;
    y_rel: number;
}

type Operation =
    | {
    type: "delete";
    node_id: string;
}
    | {
    type: "create";
    attributes?: HierarchyNodeAttributeModel[];
    references?: DrawingObjectReferenceModel[];
    aliases?: string[];
}
    | {
    type: "update";
    node_id: string;
    attributes?: HierarchyNodeAttributeModel[];
    references?: DrawingObjectReferenceModel[];
    aliases?: string[];
};

interface CommandModel {
    id: string;
    uid: UniversalNodeIdModel;
    parent_uid?: UniversalNodeIdModel;
    operation: Operation;
}

function PreviewResultsTable({
    result,
    selectedCommands,
    onSelectionChange,
    hierarchy
}: {
    result: CommandModel[],
    selectedCommands: string[],
    onSelectionChange: (commands: string[]) => void,
    hierarchy: Hierarchy,
}) {
    const columns: ColumnsType<CommandModel> = [
        {
            title: "Select",
            key: "select",
            width: 60,
            render: (_, record) => (
                <Checkbox
                    checked={selectedCommands.includes(record.id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            onSelectionChange([...selectedCommands, record.id]);
                        } else {
                            onSelectionChange(selectedCommands.filter(id => id !== record.id));
                        }
                    }}
                />
            )
        },
        {
            title: "Operation",
            dataIndex: ["operation", "type"],
            key: "operation",
            width: 100,
            render: (type: string) => (
                <span style={{
                    textTransform: "capitalize",
                    fontWeight: "bold",
                    color: type === "create" ? "#52c41a" :
                        type === "update" ? "#1890ff" :
                            type === "delete" ? "#ff4d4f" : "#000000"
                }}>
                    {type}
                </span>
            )
        },
        {
            title: "Node ID",
            dataIndex: ["uid"],
            key: "nodeId",
            width: 120,
            render: (uid: UniversalNodeIdModel, _) => {
                return (
                    <span style={{color: "#999"}}>
                        {uid.label} {uid.text}
                    </span>
                );
            }
        },
        {
            title: "Parent ID",
            dataIndex: ["parent_uid"],
            key: "parentId",
            width: 120,
            render: (uid?: UniversalNodeIdModel) => {
                if (uid != null) {
                    return (
                        <span style={{color: "#999"}}>
                            {uid.label} {uid.text}
                        </span>
                    );
                } else {
                    return (
                        <span style={{color: "#999"}}>(orphan)</span>
                    );
                }
            }
        },
        {
            title: "Attributes",
            dataIndex: ["operation", "attributes"],
            key: "attributes",
            render: (attributes: HierarchyNodeAttributeModel[] = [], record: CommandModel) => {
                if (!attributes.length) {
                    return <span style={{color: '#999'}}>No attributes</span>;
                }
                return (
                    <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>
                        {attributes.map((attr, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center"
                                }}
                            >
                                <span style={{color: "#666"}}>{attr.key}:</span>
                                <span>{attr.value}</span>
                            </div>
                        ))}
                    </div>
                );
            }
        },
        ...(hierarchy.useV2References ? ([
            {
                title: "Aliases",
                dataIndex: ["operation", "aliases"],
                key: "aliases",
                render: (aliases: string[] = []) => {
                    if (!aliases.length) {
                        return <span style={{ color: '#999' }}>No aliases</span>;
                    }
                    return (
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            {aliases.map((alias, index) => (
                                <span key={index}>{alias}</span>
                            ))}
                        </div>
                    );
                }
            }
        ]) : []),
        {
            title: "References",
            dataIndex: ["operation", "references"],
            key: "references",
            render: (references: DrawingObjectReferenceModel[] = [], _) => {
                if (!references.length) {
                    return <span style={{ color: '#999' }}>No references</span>;
                }

                return (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        {references.map((ref, index) => (
                            <div
                                key={index}
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    padding: "4px 8px",
                                    border: "1px solid #eee",
                                    borderRadius: "4px"
                                }}
                            >
                                <div><strong>ID:</strong> {ref.id || <span style={{ color: "#999" }}>Unknown</span>}</div>
                                <div><strong>Page ID:</strong> {ref.page_id}</div>
                                <div><strong>X:</strong> {ref.x_rel}</div>
                                <div><strong>Y:</strong> {ref.y_rel}</div>
                            </div>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <Table
            dataSource={result}
            columns={columns}
            rowKey={(record) => record.id}
            size="small"
            pagination={false}
            style={{marginBottom: "16px"}}
        />
    );
}